import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ChipRow from '../Components/ChipRow'
import FeedList from '../Components/FeedList'
import { RefreshRow } from '../Components/FeedStatus'
import { CloseIcon, PlusIcon, StarIcon } from '../Components/Icons'
import { useRail } from '../Components/Layout'
import TopicTag from '../Components/TopicTag'
import { TopicRow } from '../Components/Trending'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import { useFeed } from '../Hooks/useFeed'
import { useDebounced } from '../Hooks/useSearch'
import useTapToRefresh from '../Hooks/useTapToRefresh'
import { t } from '../i18n'
import { api } from '../Services/api'
import { topicQueryValue, useFollow } from '../State/Follow'
import { useTrending } from '../State/refData'
import { useUi } from '../State/Ui'
import { track } from '../Utils/analytics'
import { norm } from '../Utils/text'

const HIGHLIGHT_MS = 2500
const MAX_LEN = 60
const suggestCache = new Map()

/* Topics and "add the text itself" while typing (spec 5.7, from 2 letters). */
function useSuggest(text) {
  const q = useDebounced(text.trim(), 250)
  const key = norm(q)
  const [state, setState] = useState({ key: '', data: null })
  useEffect(() => {
    if (key.length < 2) return undefined
    if (suggestCache.has(key)) {
      setState({ key, data: suggestCache.get(key) })
      return undefined
    }
    let alive = true
    api
      .suggest(q)
      .then((data) => {
        suggestCache.set(key, data)
        if (alive) setState({ key, data })
      })
      .catch(() => alive && setState({ key, data: null }))
    return () => {
      alive = false
    }
  }, [key, q])
  // Only an answer for what's typed now counts.
  return norm(text.trim()) === state.key ? state.data : null
}

function SuggestionPanel({ data, text, cursor, onTopic, onKeyword, isFollowed }) {
  const showKeyword = !data.exactTopicMatch && data.keyword && !data.keyword.error
  return (
    <div
      className="suggest-panel"
      role="listbox"
      id="suggest-list"
      aria-label={t('myTopics.suggestions')}
    >
      {data.topics.map((topic, i) => {
        const followed = isFollowed(topic)
        return (
          <button
            key={topic.slug}
            type="button"
            role="option"
            id={`suggest-${i}`}
            aria-selected={cursor === i}
            className={`suggest-row ${cursor === i ? 'active' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onTopic(topic)}
          >
            <StarIcon size={18} className="muted-icon" />
            <span className="suggest-text">
              <span className="suggest-name">{topic.shortTitle || topic.title}</span>
              <span className="suggest-meta">
                {t('myTopics.topicMeta', { count: topic.articleCount ?? topic.storyCount })}
              </span>
            </span>
            {followed ? (
              <span className="suggest-following">{t('myTopics.following')}</span>
            ) : (
              <span className="btn btn-primary btn-small" aria-hidden="true">
                <PlusIcon size={14} strokeWidth={2.5} />
                {t('myTopics.add')}
              </span>
            )}
          </button>
        )
      })}
      {showKeyword && (
        <button
          type="button"
          role="option"
          id={`suggest-${data.topics.length}`}
          aria-selected={cursor === data.topics.length}
          className={`suggest-row suggest-keyword ${cursor === data.topics.length ? 'active' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onKeyword(data.keyword.text || text)}
        >
          <span className="suggest-plus" aria-hidden="true">
            <PlusIcon size={16} strokeWidth={2.5} />
          </span>
          <span className="suggest-text">
            <span className="suggest-name">{t('myTopics.addKeyword', { query: text })}</span>
            <span className="suggest-meta">
              {data.keyword.count > 0
                ? t('myTopics.keywordCount', { count: data.keyword.count })
                : t('myTopics.keywordNone')}
            </span>
          </span>
        </button>
      )}
    </div>
  )
}

export default function MyTopics() {
  const follow = useFollow()
  const { notify } = useUi()
  const [value, setValue] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const [cursor, setCursor] = useState(-1)
  const [active, setActive] = useState(null) // selected chip's key, null = "Sve moje teme"
  const [highlight, setHighlight] = useState(null)
  const timer = useRef(null)
  const input = useRef(null)
  const empty = follow.topics.length === 0
  useRail({ empty, ad: 'myTopics' })
  useDocumentTitle(t('myTopics.title'))
  useEffect(() => () => clearTimeout(timer.current), [])

  const text = value.trim()
  const data = useSuggest(value)
  const panel = panelOpen && text.length >= 2 && data
  const options = panel
    ? [
        ...data.topics.map((topic) => () => addTopic(topic, 'suggest_panel')),
        ...(!data.exactTopicMatch && data.keyword && !data.keyword.error
          ? [() => addKeyword(data.keyword.text || text, 'suggest_panel')]
          : []),
      ]
    : []
  useEffect(() => setCursor(-1), [text])

  // A removed topic can't stay selected.
  useEffect(() => {
    if (active && !follow.topics.some((x) => x.key === active)) setActive(null)
  }, [active, follow.topics])

  const flash = (key) => {
    clearTimeout(timer.current)
    setHighlight(key)
    timer.current = setTimeout(() => setHighlight(null), HIGHLIGHT_MS)
  }
  const done = (result) => {
    flash(result.key)
    setValue('')
    setPanelOpen(false)
  }
  // entry: suggest_panel | maybe_interested | input (analytics spec 5.3)
  function addTopic(topic, entry) {
    const result = follow.addTopic({ slug: topic.slug, title: topic.shortTitle || topic.title })
    if (result.added) track('topic_follow', { topic_slug: topic.slug, entry })
    done(result)
  }
  function addKeyword(keyword, entry) {
    const result = follow.addKeyword(keyword.slice(0, MAX_LEN))
    if (result.added) {
      track('custom_topic_add', {
        keyword: norm(keyword),
        count_7d: data?.keyword && !data.keyword.error ? data.keyword.count ?? null : null,
        entry,
      })
    }
    done(result)
  }
  const select = (entry) => {
    track('my_topics_filter', {
      ...(entry.type === 'topic' ? { topic_slug: entry.slug } : { keyword: norm(entry.title) }),
      is_custom: entry.type !== 'topic',
    })
    setActive(entry.key)
  }

  const submit = (e) => {
    e.preventDefault()
    if (!text) return
    if (cursor >= 0 && options[cursor]) {
      options[cursor]()
      return
    }
    // Typed exactly as an existing topic: follow the topic, not the words.
    const exact =
      data?.exactTopicMatch &&
      data.topics.find((x) => [x.title, x.shortTitle].some((n) => norm(n) === norm(text)))
    if (exact) addTopic(exact, 'input')
    else addKeyword(data?.keyword?.text && !data.keyword.error ? data.keyword.text : text, 'input')
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setPanelOpen(false)
      return
    }
    if (!options.length || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return
    e.preventDefault()
    setCursor((c) =>
      e.key === 'ArrowDown' ? (c + 1) % options.length : c <= 0 ? options.length - 1 : c - 1
    )
  }

  const remove = (entry) => {
    const removed = follow.removeTopic(entry.key)
    if (!removed) return
    track('topic_unfollow', {
      ...(entry.type === 'topic' ? { topic_slug: entry.slug } : { keyword: norm(entry.title) }),
      is_custom: entry.type !== 'topic',
    })
    notify(t('myTopics.removed'), {
      label: t('myTopics.undo'),
      onClick: () => follow.restoreTopic(removed),
    })
  }

  const isFollowed = (topic) =>
    Boolean(
      follow.followedTopic(topic.slug) || follow.followedKeyword(topic.shortTitle || topic.title)
    )

  // "Možda te zanima": current topics not followed yet (up to 6).
  const trending = useTrending({ limit: 15 })
  const mayInterest = trending.topics.filter((topic) => !isFollowed(topic)).slice(0, 6)

  const selected = active ? follow.topics.find((x) => x.key === active) : null
  const entries = selected ? [selected] : follow.topics
  const values = entries.map(topicQueryValue)
  const feed = useFeed(
    `my:${values.join('|')}:${follow.sources.join(',')}`,
    ({ cursor: c, refresh }) =>
      api.myTopicsFeed(values, { cursor: c, followed: follow.sources, refresh }),
    { enabled: values.length > 0, eventProps: { chip: active || 'all' } }
  )
  const { row, tap } = useTapToRefresh(feed, 'chip_tap')

  // A followed topic merged into another (spec 9.7a): the API reads the old
  // slug as the new topic; follow that one instead, keeping the selection.
  const resolved = feed.extra?.topics
  useEffect(() => {
    if (!Array.isArray(resolved)) return
    resolved.forEach((r) => {
      if (r.type !== 'topic' || !r.slug || r.slug === r.input) return
      const was = follow.followedTopic(r.input)
      const key = follow.retargetTopic(r.input, {
        slug: r.slug,
        title: r.shortTitle || r.title,
      })
      if (key && was && active === was.key) setActive(key)
    })
    // Only when the API answers; follow/active are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved])

  /* The tag on each card (spec 5.7): on "Sve moje teme" the followed topic
     that brought the story in, as the reader named it ("Željezničar"); a
     click selects that chip. None when one topic is selected. */
  const tagFor = (story) => {
    if (selected) return null
    const entry = follow.topics.find((e) => topicQueryValue(e) === story.matchedTopic)
    if (!entry) return story.topic ? <TopicTag topic={story.topic} /> : null
    return (
      <TopicTag
        label={entry.title}
        onSelect={() => {
          select(entry)
          window.scrollTo({ top: 0 })
        }}
      />
    )
  }
  // An archived topic (no story in 15 days, spec 9.7a) says so.
  const archived = Boolean(selected && resolved?.[0]?.archived)

  return (
    <div className="my-topics">
      <h1 className="screen-title">{t('myTopics.title')}</h1>
      <div className="add-topic">
        <form className={`add-topic-form ${text ? 'has-text' : ''}`} onSubmit={submit}>
          <input
            ref={input}
            type="text"
            value={value}
            maxLength={MAX_LEN}
            placeholder={t('myTopics.placeholder')}
            aria-label={t('myTopics.inputLabel')}
            aria-autocomplete="list"
            aria-controls={panel ? 'suggest-list' : undefined}
            aria-activedescendant={cursor >= 0 ? `suggest-${cursor}` : undefined}
            autoComplete="off"
            onChange={(e) => {
              setValue(e.target.value)
              setPanelOpen(true)
            }}
            onFocus={() => setPanelOpen(true)}
            onBlur={() => setPanelOpen(false)}
            onKeyDown={onKeyDown}
          />
          <button type="submit" className="btn btn-primary" disabled={!text}>
            <PlusIcon size={16} strokeWidth={2.5} />
            {t('myTopics.add')}
          </button>
        </form>
        {panel && (
          <SuggestionPanel
            data={data}
            text={text}
            cursor={cursor}
            onTopic={(topic) => addTopic(topic, 'suggest_panel')}
            onKeyword={(keyword) => addKeyword(keyword, 'suggest_panel')}
            isFollowed={isFollowed}
          />
        )}
      </div>

      {empty ? (
        <>
          <div className="empty-card">
            <StarIcon size={28} className="empty-star" />
            <h2>{t('myTopics.emptyTitle')}</h2>
            <p>{t('myTopics.emptyText')}</p>
          </div>
          {trending.topics.length > 0 && (
            <section className="explore-section">
              <h2 className="section-title">{t('trending.title')}</h2>
              <ol className="topic-list">
                {trending.topics.slice(0, 6).map((topic, i) => (
                  <TopicRow
                    key={topic.slug}
                    topic={topic}
                    rank={i + 1}
                    follow
                    entry="trending_block"
                    followEntry="empty_state"
                  />
                ))}
              </ol>
            </section>
          )}
          <div className="center-block">
            <Link to="/istrazi" className="btn btn-secondary">
              {t('myTopics.exploreAll')}
            </Link>
          </div>
        </>
      ) : (
        <>
          {!panel && !text && mayInterest.length > 0 && (
            <section className="may-interest">
              <h2 className="section-title">{t('myTopics.mayInterest')}</h2>
              <ChipRow label={t('myTopics.mayInterest')}>
                {mayInterest.map((topic) => (
                  <button
                    key={topic.slug}
                    type="button"
                    className="chip chip-dashed"
                    aria-label={t('myTopics.addTopic', { name: topic.shortTitle || topic.title })}
                    onClick={() => addTopic(topic, 'maybe_interested')}
                  >
                    <PlusIcon size={14} strokeWidth={2.5} />
                    <span>{topic.shortTitle || topic.title}</span>
                  </button>
                ))}
              </ChipRow>
            </section>
          )}

          <div className="sticky-bar">
            <ChipRow label={t('myTopics.chipsLabel')} activeKey={`${active}:${highlight}`}>
              <button
                type="button"
                className={`chip ${active === null ? 'chip-active' : ''}`}
                aria-pressed={active === null}
                onClick={() => (active === null ? tap() : setActive(null))}
              >
                {t('myTopics.all')}
              </button>
              {follow.topics.map((entry) => (
                <span
                  key={entry.key}
                  className={`chip chip-removable ${active === entry.key ? 'chip-active' : ''} ${
                    highlight === entry.key ? 'chip-highlight' : ''
                  }`}
                  data-highlight={highlight === entry.key ? 'true' : undefined}
                >
                  <button
                    type="button"
                    className="chip-label"
                    aria-pressed={active === entry.key}
                    onClick={() => (active === entry.key ? tap() : select(entry))}
                  >
                    {entry.title}
                  </button>
                  <button
                    type="button"
                    className="chip-remove"
                    aria-label={t('myTopics.remove', { name: entry.title })}
                    onClick={() => remove(entry)}
                  >
                    <CloseIcon size={14} strokeWidth={2.5} />
                  </button>
                </span>
              ))}
            </ChipRow>
          </div>
          <RefreshRow state={row} />
          <FeedList
            adSurface="myTopics"
            feed={feed}
            tagFor={tagFor}
            empty={<p>{t(archived ? 'myTopics.noNewNews' : 'myTopics.noNews')}</p>}
          />
        </>
      )}
    </div>
  )
}
